import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Save, Loader2, ShoppingBag, Settings, X, Barcode, ImagePlus, UserPlus, Link2 } from "lucide-react";
import { toast } from "sonner";
import { formatCLP, imgSrc } from "@/lib/tienda";

const VACIO = { nombre: "", sku: "", codigo: "", codigo_barras: "", categoria: "",
  descripcion_corta: "", descripcion: "", precio: "", precio_oferta: "", costo: "",
  stock: "", imagenes: [], video_url: "", activo: true, destacado: false, requiere_envio: true };

// Definido FUERA del componente: si se define adentro, React lo trata como un
// componente nuevo en cada render y el input pierde el foco tras cada tecla.
const F = ({ edit, setEdit, label, k, type = "text", ph = "" }) => (
  <div><Label className="text-xs">{label}</Label>
    <Input type={type} value={edit[k] ?? ""} placeholder={ph}
      onChange={(e) => setEdit((p) => ({ ...p, [k]: e.target.value }))}
      className="h-10 bg-white no-uppercase mt-1" /></div>
);

export default function TiendaAdmin() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [tab, setTab] = useState("productos");
  const [productos, setProductos] = useState([]);
  const [prodsListos, setProdsListos] = useState(false);
  const [repuestos, setRepuestos] = useState([]);
  const [picker, setPicker] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [tecnicos, setTecnicos] = useState([]);
  const [asignar, setAsignar] = useState(null); // {producto, tecnico_id, cantidad}
  const [busy, setBusy] = useState(false);
  const imgRef = useRef(null);
  const venderRef = useRef(false);

  const load = () => {
    api.get("/api/tienda/admin/productos").then((r) => { setProductos(r.data); setProdsListos(true); }).catch(() => {});
    api.get("/api/repuestos").then((r) => setRepuestos(r.data)).catch(() => {});
    api.get("/api/tienda/admin/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/api/tienda/inventario").then((r) => setTecnicos(r.data.tecnicos || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Publica un repuesto del catálogo en la tienda (o abre su producto si ya existe)
  const desdeRepuesto = (rep) => {
    const existente = productos.find((p) => p.repuesto_id === rep.id || (rep.sku && p.sku === rep.sku));
    setEdit(existente ? { ...existente } : {
      ...VACIO, nombre: rep.nombre || "", sku: rep.sku || "", categoria: rep.categoria || "",
      precio: rep.precio_venta || "", repuesto_id: rep.id,
    });
    setPicker(false); setTab("productos");
  };

  // Llegada desde Catálogo de Repuestos → botón "Vender en tienda" (?vender=<id>)
  useEffect(() => {
    if (venderRef.current || repuestos.length === 0 || !prodsListos) return;
    venderRef.current = true;
    const id = new URLSearchParams(window.location.search).get("vender");
    const rep = id && repuestos.find((r) => r.id === id);
    if (rep) desdeRepuesto(rep);
    // eslint-disable-next-line
  }, [repuestos, prodsListos]);

  const subirImagen = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/api/tienda/admin/imagenes", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setEdit((p) => ({ ...p, imagenes: [...(p.imagenes || []), r.data.id] }));
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir imagen"); }
    finally { setBusy(false); }
  };

  const guardar = async () => {
    if (!edit.nombre?.trim()) return toast.error("El nombre es obligatorio");
    setBusy(true);
    try {
      if (edit.id) await api.put(`/api/tienda/admin/productos/${edit.id}`, edit);
      else await api.post("/api/tienda/admin/productos", edit);
      toast.success("Producto guardado");
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const eliminar = async (p) => {
    if (!window.confirm(`¿Eliminar ${p.nombre}?`)) return;
    try {
      const r = await api.delete(`/api/tienda/admin/productos/${p.id}`);
      toast.success(r.data.message); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const guardarConfig = async () => {
    setBusy(true);
    try {
      await api.put("/api/tienda/admin/config", cfg);
      toast.success("Configuración guardada"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const doAsignar = async () => {
    if (!asignar.tecnico_id || !asignar.cantidad) return toast.error("Elige técnico y cantidad");
    setBusy(true);
    try {
      await api.post(`/api/tienda/admin/productos/${asignar.producto.id}/asignar`,
        { tecnico_id: asignar.tecnico_id, cantidad: Number(asignar.cantidad) });
      toast.success("Inventario asignado");
      setAsignar(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <ShoppingBag className="h-5 w-5 text-[#E0653A] flex-shrink-0" />
            <span className="text-sm font-semibold text-[#384091] truncate">Tienda · Productos</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant={tab === "productos" ? "default" : "outline"} size="sm"
              className={tab === "productos" ? "bg-[#384091] text-white" : ""}
              onClick={() => setTab("productos")}>Productos</Button>
            <Button variant={tab === "config" ? "default" : "outline"} size="sm"
              className={tab === "config" ? "bg-[#384091] text-white" : ""}
              onClick={() => setTab("config")}>
              <Settings className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Configuración</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-5 space-y-4">
        {tab === "config" && cfg && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5 space-y-4">
              <div>
                <h2 className="font-bold text-[#384091]">Mercado Pago</h2>
                <p className="text-xs text-gray-400">Credenciales de producción (mercadopago.cl → Tus integraciones → Credenciales).</p>
                <div className="grid grid-cols-1 gap-3 mt-3">
                  <div><Label className="text-xs">Access Token {cfg.mp_access_token_set && <Badge className="ml-1 bg-emerald-50 text-emerald-700 border-0 text-[10px]">configurado</Badge>}</Label>
                    <Input value={cfg.mp_access_token || ""} placeholder="APP_USR-…"
                      onChange={(e) => setCfg((p) => ({ ...p, mp_access_token: e.target.value }))}
                      className="h-10 bg-white no-uppercase mt-1" /></div>
                  <div><Label className="text-xs">Public Key</Label>
                    <Input value={cfg.mp_public_key || ""} placeholder="APP_USR-…"
                      onChange={(e) => setCfg((p) => ({ ...p, mp_public_key: e.target.value }))}
                      className="h-10 bg-white no-uppercase mt-1" /></div>
                </div>
              </div>
              <div>
                <h2 className="font-bold text-[#384091]">Tarifas de despacho (CLP)</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                  {[["rm", "R. Metropolitana"], ["regiones", "Otras regiones"], ["extremas", "Zonas extremas"], ["gratis_desde", "Gratis desde"]].map(([k, l]) => (
                    <div key={k}><Label className="text-xs">{l}</Label>
                      <Input type="number" value={cfg.envio?.[k] ?? ""}
                        onChange={(e) => setCfg((p) => ({ ...p, envio: { ...p.envio, [k]: e.target.value } }))}
                        className="h-10 bg-white no-uppercase mt-1" /></div>
                  ))}
                </div>
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <Checkbox checked={!!cfg.envio?.retiro_tienda}
                    onCheckedChange={(v) => setCfg((p) => ({ ...p, envio: { ...p.envio, retiro_tienda: !!v } }))} />
                  <span className="text-sm">Permitir retiro en tienda (gratis)</span>
                </label>
              </div>
              <div><Label className="text-xs">Correo para avisos de pedidos</Label>
                <Input value={cfg.notif_email || ""} onChange={(e) => setCfg((p) => ({ ...p, notif_email: e.target.value }))}
                  className="h-10 bg-white no-uppercase mt-1" /></div>
              <Button className="bg-[#384091] text-white" onClick={guardarConfig} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Guardar configuración</>}
              </Button>
            </CardContent>
          </Card>
        )}

        {tab === "productos" && (
          <>
            {!edit && !picker && (
              <Button className="bg-[#E0653A] hover:bg-[#c85530] text-white" onClick={() => { setPicker(true); setPickerQ(""); }}>
                <Plus className="h-4 w-4 mr-1" /> Nuevo producto
              </Button>
            )}

            {/* Selector desde el Catálogo de Repuestos (fuente única) */}
            {picker && !edit && (
              <Card className="border-0 shadow-md ring-2 ring-[#E0653A]/30">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-[#E0653A] flex items-center gap-2">
                      <Link2 className="h-4 w-4" /> Publicar desde el Catálogo de Repuestos
                    </h3>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setPicker(false)}><X className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-xs text-gray-400 -mt-1">
                    Los repuestos se crean en el Catálogo y desde aquí se publican a la venta.
                    Quedan enlazados: nombre, SKU y precio se mantienen sincronizados.
                  </p>
                  <Input value={pickerQ} onChange={(e) => setPickerQ(e.target.value)} autoFocus
                    placeholder="Busca por SKU, nombre o categoría…" className="h-10 bg-white no-uppercase" />
                  <div className="max-h-64 overflow-y-auto divide-y rounded-xl border">
                    {repuestos
                      .filter((r) => !pickerQ || `${r.sku} ${r.nombre} ${r.categoria}`.toLowerCase().includes(pickerQ.toLowerCase()))
                      .slice(0, 30).map((r) => {
                        const pub = productos.find((p) => p.repuesto_id === r.id);
                        return (
                          <button key={r.id} type="button" onClick={() => desdeRepuesto(r)}
                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#E0653A]/5 flex items-center gap-2">
                            <span className="font-mono text-xs text-[#0FB4B7] font-bold w-24 flex-shrink-0">{r.sku}</span>
                            <span className="flex-1 truncate">{r.nombre}</span>
                            {pub && <Badge className={`border-0 text-[10px] flex-shrink-0 ${pub.activo ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{pub.activo ? "EN TIENDA" : "OCULTO"}</Badge>}
                          </button>
                        );
                      })}
                    {repuestos.length === 0 && (
                      <p className="text-center text-sm text-gray-400 py-6">El catálogo está vacío. Créalos en Datos maestros → Repuestos.</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-px bg-gray-100 flex-1" /><span className="text-[11px] text-gray-400">o</span><div className="h-px bg-gray-100 flex-1" />
                  </div>
                  <Button variant="outline" className="w-full" onClick={() => { setEdit({ ...VACIO }); setPicker(false); }}>
                    Crear producto libre (al guardar con SKU se agrega al catálogo)
                  </Button>
                </CardContent>
              </Card>
            )}

            {edit && (
              <Card className="border-0 shadow-md ring-2 ring-[#E0653A]/30">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-[#E0653A]">{edit.id ? "Editar producto" : "Nuevo producto"}</h3>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEdit(null)}><X className="h-4 w-4" /></Button>
                  </div>
                  {edit.repuesto_id ? (
                    <p className="text-[11px] text-emerald-700 bg-emerald-50 rounded-lg px-3 py-1.5 flex items-center gap-1.5">
                      <Link2 className="h-3 w-3 flex-shrink-0" /> Enlazado al Catálogo de Repuestos — nombre, SKU y precio se mantienen sincronizados.
                    </p>
                  ) : (
                    <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-1.5">
                      Sin enlace al catálogo: asigna un SKU y al guardar se creará (o enlazará) su ficha en el Catálogo de Repuestos automáticamente.
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2"><F edit={edit} setEdit={setEdit} label="Nombre *" k="nombre" /></div>
                    <F edit={edit} setEdit={setEdit} label="SKU" k="sku" ph="TUR-NSK-01" />
                    <F edit={edit} setEdit={setEdit} label="Código interno" k="codigo" />
                    <div><Label className="text-xs flex items-center gap-1"><Barcode className="h-3.5 w-3.5" /> Código de barras (pistolea aquí)</Label>
                      <Input value={edit.codigo_barras ?? ""} placeholder="Escanea o escribe el código"
                        onChange={(e) => setEdit((p) => ({ ...p, codigo_barras: e.target.value }))}
                        className="h-10 bg-white no-uppercase mt-1" /></div>
                    <F edit={edit} setEdit={setEdit} label="Categoría" k="categoria" ph="REPUESTOS / INSUMOS…" />
                    <F edit={edit} setEdit={setEdit} label="Precio venta (CLP)" k="precio" type="number" />
                    <F edit={edit} setEdit={setEdit} label="Precio oferta (opcional)" k="precio_oferta" type="number" />
                    <F edit={edit} setEdit={setEdit} label="Costo (CLP) — para margen" k="costo" type="number" />
                    <F edit={edit} setEdit={setEdit} label="Stock bodega" k="stock" type="number" />
                    <div className="sm:col-span-2"><F edit={edit} setEdit={setEdit} label="Video (link de YouTube, opcional)" k="video_url" ph="https://youtu.be/…" /></div>
                    <div className="sm:col-span-2"><Label className="text-xs">Descripción corta (aparece en la tarjeta)</Label>
                      <Input value={edit.descripcion_corta ?? ""} maxLength={200}
                        onChange={(e) => setEdit((p) => ({ ...p, descripcion_corta: e.target.value }))}
                        className="h-10 bg-white mt-1" /></div>
                    <div className="sm:col-span-2"><Label className="text-xs">Descripción ampliada (al hacer clic en el producto)</Label>
                      <Textarea rows={4} value={edit.descripcion ?? ""}
                        onChange={(e) => setEdit((p) => ({ ...p, descripcion: e.target.value }))}
                        className="bg-white mt-1" /></div>
                  </div>

                  {/* imágenes múltiples */}
                  <div>
                    <Label className="text-xs">Imágenes (la primera es la portada)</Label>
                    <input ref={imgRef} type="file" accept="image/*" className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; subirImagen(f); }} />
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(edit.imagenes || []).map((id, i) => (
                        <div key={id} className="relative h-20 w-20 rounded-lg overflow-hidden border">
                          <img src={imgSrc(id)} alt="" className="w-full h-full object-cover" />
                          {i === 0 && <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] text-center">PORTADA</span>}
                          <button className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
                            onClick={() => setEdit((p) => ({ ...p, imagenes: p.imagenes.filter((x) => x !== id) }))}>
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" className="h-20 w-20 flex-col gap-1"
                        onClick={() => imgRef.current?.click()} disabled={busy}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                        <span className="text-[10px]">Agregar</span>
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-5">
                    {[["activo", "Visible en la tienda"], ["destacado", "Destacado"], ["requiere_envio", "Requiere despacho"]].map(([k, l]) => (
                      <label key={k} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox checked={!!edit[k]} onCheckedChange={(v) => setEdit((p) => ({ ...p, [k]: !!v }))} />
                        <span className="text-sm">{l}</span>
                      </label>
                    ))}
                  </div>
                  <Button className="w-full h-11 bg-[#E0653A] hover:bg-[#c85530] text-white" onClick={guardar} disabled={busy}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar producto"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* asignar a técnico */}
            {asignar && (
              <Card className="border-0 shadow-md ring-2 ring-[#0FB4B7]/40">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-[#0D999C]">Asignar "{asignar.producto.nombre}" a técnico</h3>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setAsignar(null)}><X className="h-4 w-4" /></Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2"><Label className="text-xs">Técnico</Label>
                      <Select value={asignar.tecnico_id} onValueChange={(v) => setAsignar((p) => ({ ...p, tecnico_id: v }))}>
                        <SelectTrigger className="h-10 bg-white mt-1"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {tecnicos.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select></div>
                    <div><Label className="text-xs">Cantidad (− devuelve a bodega)</Label>
                      <Input type="number" value={asignar.cantidad}
                        onChange={(e) => setAsignar((p) => ({ ...p, cantidad: e.target.value }))}
                        className="h-10 bg-white no-uppercase mt-1" /></div>
                  </div>
                  <Button className="bg-[#0FB4B7] text-white" onClick={doAsignar} disabled={busy}>Asignar</Button>
                </CardContent>
              </Card>
            )}

            <div className="space-y-2">
              {productos.map((p) => (
                <Card key={p.id} className="border-0 shadow-sm">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="h-12 w-12 rounded-lg bg-gray-50 overflow-hidden flex-shrink-0">
                      {p.imagenes?.[0] && <img src={imgSrc(p.imagenes[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {p.nombre}
                        {!p.activo && <Badge className="ml-2 bg-gray-100 text-gray-500 border-0 text-[10px]">oculto</Badge>}
                        {p.repuesto_id && <Badge className="ml-2 bg-[#0FB4B7]/10 text-[#0D999C] border-0 text-[10px]">CATÁLOGO</Badge>}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {p.sku || "sin SKU"} · {formatCLP(p.precio)} · stock bodega: <b>{p.stock}</b>
                        {Object.values(p.stock_tecnicos || {}).some(Boolean) &&
                          ` · técnicos: ${Object.values(p.stock_tecnicos).reduce((a, b) => a + b, 0)}`}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" title="Asignar a técnico"
                      onClick={() => setAsignar({ producto: p, tecnico_id: "", cantidad: 1 })}>
                      <UserPlus className="h-4 w-4 text-[#0FB4B7]" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setEdit({ ...p })}>Editar</Button>
                    <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600" onClick={() => eliminar(p)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
              {productos.length === 0 && !edit && (
                <p className="text-center text-gray-400 py-10 text-sm">Sin productos aún. Crea el primero para activar la tienda.</p>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
